import { API_URL } from '@/constants/api';
import { readJson } from '@/shared/utils/json';
import type { CatSubscription, CatSubscriptionsResponse, UpdateCatInfoPayload } from '../types';

type CatSubscriptionsApiResponse = {
  data?: CatSubscription[];
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
  meta?: {
    current_page?: number;
    last_page?: number;
    per_page?: number;
    total?: number;
  };
};

type ErrorResponse = {
  message?: string;
};

/**
 * Fetch cat subscriptions for a specific customer
 */
export const fetchCatSubscriptions = async (
  shopifyCustomerId: string,
  page: number = 1,
  perPage: number = 10,
): Promise<CatSubscriptionsResponse> => {
  const response = await fetch(
    `${API_URL}/admin/customers/${shopifyCustomerId}/cats?page=${page}&perPage=${perPage}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch cat subscriptions');
  }

  const json = await readJson<CatSubscriptionsApiResponse>(response);
  const pagination = json.meta ?? json;

  return {
    items: json.data || [],
    pagination: {
      total: pagination.total || 0,
      currentPage: pagination.current_page || page,
      perPage: pagination.per_page || perPage,
      totalPages: pagination.last_page || 1,
    },
  };
};

export const updateCatInfo = async (
  shopifyCustomerId: string,
  catId: number,
  data: UpdateCatInfoPayload,
): Promise<CatSubscription> => {
  const response = await fetch(`${API_URL}/admin/customers/${shopifyCustomerId}/cats/${catId}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const responseData = await readJson<ErrorResponse>(response).catch(() => null);
    throw new Error(responseData?.message || 'Failed to update cat information');
  }

  return readJson<CatSubscription>(response);
};
